import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useAuth } from '../AuthContext';
import { SIGNUP_MUTATION } from '../graphql/auth';
import './AuthPages.css';

export default function SignupPage() {
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    domain: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { login } = useAuth();
  const navigate = useNavigate();

  const [signup, { loading }] = useMutation(SIGNUP_MUTATION, {
    onCompleted: (data) => {
      const { token } = data.signup;
      login(token);
      navigate('/');
    },
    onError: (error) => {
      setErrors({
        submit: error.graphQLErrors[0]?.message || error.message || 'Signup failed',
      });
    },
  });

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, hyphens, and underscores';
    }

    // Domain validation (shop name)
    if (!formData.domain.trim()) {
      newErrors.domain = 'Shop name is required';
    } else if (formData.domain.length < 3) {
      newErrors.domain = 'Shop name must be at least 3 characters';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and numbers';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    signup({
      variables: {
        email: formData.email.trim(),
        username: formData.username.trim(),
        domain: formData.domain.trim(),
        password: formData.password,
      },
    });
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>DesignsDesigns ERP</h1>
          <p>Quote to Work Order Management</p>
        </div>

        <h2>Create Account</h2>
        <p className="auth-subtitle">Set up your drapery shop ERP system</p>

        {errors.submit && (
          <div className="error-message" role="alert">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              className={errors.email ? 'input-error' : ''}
              placeholder="name@company.com"
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="username">Your Name / Username</label>
            <input
              id="username"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              disabled={loading}
              className={errors.username ? 'input-error' : ''}
              placeholder="johndoe"
            />
            {errors.username && <span className="field-error">{errors.username}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="domain">Shop Name</label>
            <input
              id="domain"
              type="text"
              name="domain"
              value={formData.domain}
              onChange={handleChange}
              disabled={loading}
              className={errors.domain ? 'input-error' : ''}
              placeholder="ABC Drapery & Furniture"
            />
            {errors.domain && <span className="field-error">{errors.domain}</span>}
            <small className="field-hint">This will be your organization's unique identifier</small>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              className={errors.password ? 'input-error' : ''}
              placeholder="Enter a strong password"
            />
            {errors.password && <span className="field-error">{errors.password}</span>}
            <small className="field-hint">At least 8 characters with uppercase, lowercase, and numbers</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={loading}
              className={errors.confirmPassword ? 'input-error' : ''}
              placeholder="Re-enter your password"
            />
            {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
          </div>

          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="auth-link">
              Sign in
            </Link>
          </p>
          <p style={{ marginTop: 8, fontSize: 12 }}>
            <Link to="/privacy-policy" className="auth-link">Privacy Policy</Link>
            {' · '}
            <Link to="/terms" className="auth-link">Terms of Service</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
